import { useMutation } from '@apollo/client';
import type {
  UpdateDepartmentTypeMutation,
  UpdateDepartmentTypeMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateDepartmentTypeDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateDepartmentTypeTRPC } from './useUpdateDepartmentTypeTRPC';

export const useUpdateDepartmentType = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateDepartmentTypeGraphQL, graphqlState] = useMutation(
    UpdateDepartmentTypeDocument,
    {
      update: (cache) => {
        evictField(cache, 'department_type');
        evictField(cache, 'department_type_group');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateDepartmentTypeTRPC();

  const updateDepartmentType = async (
    variables: UpdateDepartmentTypeMutationVariables
  ): Promise<UpdateDepartmentTypeMutation> => {
    if (trpcEnabled) {
      if (!variables.OriginalTimestamp) {
        throw new Error(
          'OriginalTimestamp is required to update a department type (optimistic locking)'
        );
      }

      return trpcMutation.updateDepartmentType({
        DepartmentTypeId: variables.DepartmentTypeId,
        Name: variables.Name,
        Description: variables.Description,
        DepartmentTypeGroupId: variables.DepartmentTypeGroupId,
        OriginalTimestamp: variables.OriginalTimestamp,
      });
    }

    const result = await updateDepartmentTypeGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to update department type');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateDepartmentType,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
      data: trpcMutation.data,
    };
  }

  return {
    updateDepartmentType,
    loading: graphqlState.loading,
    error: graphqlState.error,
    data: graphqlState.data,
  };
};
